import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  formatCurveDuration,
  formatPowerValue,
  pointValue,
  usableSeries,
  hasWkg
} from '../utils/powerCurve';
import './PowerCurveChart.css';

// График кривой мощности: две линии — рекорд за год и форма последних месяцев.
// Ось длительностей логарифмическая: иначе спринт и часовой отрезок не помещаются
// на одном графике, весь интерес слипается у левого края.
const BEST_COLOR = '#667eea';
const RECENT_COLOR = '#e0625a';

// На телефоне подписать все двенадцать длительностей нельзя — они наезжают друг
// на друга. Оставляем опорные: спринт, минута, пять, двадцать и час
const MOBILE_LABELS = [1, 15, 60, 300, 1200, 3600];

// bestLabel — подпись основной линии, если это не рекорд за год (кривая одного заезда)
const PowerCurveChart = ({ best, recent, recentDays, bestLabel }) => {
  const { t } = useLanguage();
  const [unit, setUnit] = useState('watts');
  const [hovered, setHovered] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const wkgAvailable = hasWkg(best, recent);
  const activeUnit = wkgAvailable ? unit : 'watts';

  const bestPoints = useMemo(() => usableSeries(best, activeUnit), [best, activeUnit]);
  const recentPoints = useMemo(() => usableSeries(recent, activeUnit), [recent, activeUnit]);

  const geometry = useMemo(() => {
    const all = [...bestPoints, ...recentPoints];
    if (all.length < 2) return null;

    const width = isMobile ? 380 : 720;
    const height = isMobile ? 280 : 320;
    const margin = isMobile
      ? { top: 14, right: 12, bottom: 34, left: 40 }
      : { top: 20, right: 20, bottom: 44, left: 56 };
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;

    const durations = all.map(point => point[0]);
    const minDuration = Math.min(...durations);
    const maxDuration = Math.max(...durations);
    const maxValue = Math.max(...all.map(point => pointValue(point, activeUnit)));

    const logMin = Math.log(minDuration);
    const logSpan = Math.log(maxDuration) - logMin || 1;

    const x = (duration) => margin.left + ((Math.log(duration) - logMin) / logSpan) * plotWidth;
    const y = (value) => margin.top + plotHeight - (value / (maxValue * 1.08)) * plotHeight;

    const path = (points) =>
      points
        .map((point, index) => `${index === 0 ? 'M' : 'L'} ${x(point[0]).toFixed(1)} ${y(pointValue(point, activeUnit)).toFixed(1)}`)
        .join(' ');

    // Сетка по значениям: на телефоне четыре горизонтали вместо пяти
    const tickCount = isMobile ? 4 : 5;
    const ticks = Array.from({ length: tickCount }, (_, index) => (maxValue * 1.08 * (index + 1)) / tickCount);

    return { width, height, margin, plotWidth, plotHeight, x, y, path, ticks, maxValue };
  }, [bestPoints, recentPoints, activeUnit, isMobile]);

  if (!geometry) {
    return <div className="power-curve-empty">{t('power_curve.empty')}</div>;
  }

  const { width, height, margin, plotWidth, plotHeight, x, y, path, ticks } = geometry;

  // Наведение по всей ширине графика: берем ближайшую длительность по оси X.
  // На телефоне наведения нет, поэтому тот же разбор идет и по касанию
  const showValueAt = (clientX, target) => {
    const rect = target.getBoundingClientRect();
    const position = ((clientX - rect.left) / rect.width) * width;
    const reach = isMobile ? 40 : 60;

    const nearest = bestPoints.reduce((closest, point) => {
      const distance = Math.abs(x(point[0]) - position);
      return closest === null || distance < closest.distance ? { point, distance } : closest;
    }, null);

    setHovered(nearest && nearest.distance < reach ? nearest.point[0] : null);
  };

  const handleMove = (event) => showValueAt(event.clientX, event.currentTarget);

  const handleTouch = (event) => {
    const touch = event.touches[0];
    if (touch) showValueAt(touch.clientX, event.currentTarget);
  };

  const hoveredBest = bestPoints.find(point => point[0] === hovered);
  const hoveredRecent = recentPoints.find(point => point[0] === hovered);

  return (
    <div className="power-curve">
      <div className="power-curve-head">
        <div className="power-curve-legend">
          <span className="power-curve-legend-item">
            <i style={{ background: BEST_COLOR }} />
            {bestLabel || t('power_curve.legend_best')}
          </span>
          {recentPoints.length > 0 && (
            <span className="power-curve-legend-item">
              <i style={{ background: RECENT_COLOR }} />
              {t('power_curve.legend_recent').replace('{days}', recentDays || 90)}
            </span>
          )}
        </div>

        {wkgAvailable && (
          <div className="power-curve-units">
            <button
              type="button"
              className={activeUnit === 'watts' ? 'power-curve-unit-active' : ''}
              onClick={() => setUnit('watts')}
            >
              {t('power_curve.unit_watts')}
            </button>
            <button
              type="button"
              className={activeUnit === 'wkg' ? 'power-curve-unit-active' : ''}
              onClick={() => setUnit('wkg')}
            >
              {t('power_curve.unit_wkg')}
            </button>
          </div>
        )}
      </div>

      <svg
        className="power-curve-svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseMove={handleMove}
        onMouseLeave={() => setHovered(null)}
        onTouchStart={handleTouch}
        onTouchMove={handleTouch}
        onTouchEnd={() => setHovered(null)}
      >
        {ticks.map((tick, index) => (
          <g key={index}>
            <line
              x1={margin.left}
              x2={margin.left + plotWidth}
              y1={y(tick)}
              y2={y(tick)}
              className="power-curve-grid"
            />
            <text x={margin.left - 10} y={y(tick) + 4} className="power-curve-axis" textAnchor="end">
              {formatPowerValue(tick, activeUnit)}
            </text>
          </g>
        ))}

        {bestPoints
          .filter(point => !isMobile || MOBILE_LABELS.includes(point[0]))
          .map(point => (
            <text
              key={point[0]}
              x={x(point[0])}
              y={margin.top + plotHeight + (isMobile ? 20 : 24)}
              className="power-curve-axis"
              textAnchor="middle"
            >
              {formatCurveDuration(point[0], t)}
            </text>
          ))}

        <path d={path(bestPoints)} fill="none" stroke={BEST_COLOR} strokeWidth={isMobile ? 2.5 : 3} />
        {recentPoints.length > 1 && (
          <path
            d={path(recentPoints)}
            fill="none"
            stroke={RECENT_COLOR}
            strokeWidth={isMobile ? 2 : 2.5}
            strokeDasharray="6 4"
          />
        )}

        {bestPoints.map(point => (
          <circle
            key={point[0]}
            cx={x(point[0])}
            cy={y(pointValue(point, activeUnit))}
            r={isMobile ? 3 : 4}
            fill={BEST_COLOR}
          />
        ))}
        {recentPoints.map(point => (
          <circle
            key={point[0]}
            cx={x(point[0])}
            cy={y(pointValue(point, activeUnit))}
            r={isMobile ? 2.5 : 3.5}
            fill={RECENT_COLOR}
          />
        ))}

        {hovered !== null && (
          <line
            x1={x(hovered)}
            x2={x(hovered)}
            y1={margin.top}
            y2={margin.top + plotHeight}
            className="power-curve-cursor"
          />
        )}
      </svg>

      {hovered !== null && (
        <div className="power-curve-tooltip">
          <strong>{formatCurveDuration(hovered, t)}</strong>
          <span style={{ color: BEST_COLOR }}>
            {formatPowerValue(pointValue(hoveredBest, activeUnit), activeUnit)}
          </span>
          {hoveredRecent && (
            <span style={{ color: RECENT_COLOR }}>
              {formatPowerValue(pointValue(hoveredRecent, activeUnit), activeUnit)}
            </span>
          )}
          <span className="power-curve-tooltip-unit">
            {activeUnit === 'wkg' ? t('power_curve.unit_wkg') : t('power_curve.unit_watts')}
          </span>
        </div>
      )}
    </div>
  );
};

export default PowerCurveChart;
